<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="my-2 text-3xl text-teal font-semibold leading-normal">
          Guild Supplier
        </h1>
        <router-link v-if="supplier" to="/suppliers" class="text-sm text-grey-darker no-underline hover:underline">{{ breadcrumbs }}</router-link>
      </div>
    </div>

    <loader v-if="!loaded" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to view supplier listings.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col p-6 overflow-x-auto">
      <div class="w-full flex flex-wrap">
        <div class="w-full md:w-1/6 text-sm">
          <img :src="supplier.logo_url" :alt="supplier.name" class="w-48 overflow-hidden border mb-6 mr-8" />
          <div class="mb-6">
            <star-rating-component :fixed=true :value="supplier.admin_rating_override ? supplier.admin_rating : supplier.star_rating" />
            <div class="text-sm" v-if="!supplier.admin_rating_override"><strong class="font-semibold">{{ supplier.star_rating }}</strong> stars - {{ supplier.ratings_count }} {{ supplier.ratings_count === 1 ? 'review' : 'reviews' }}</div>
          </div>
          <div v-if="highestDiscount && highestDiscount.value && supplier.discount_section_type == 1" class="mb-6">
            <h4 class="mb-2">Discounts</h4>
            <div class="flex items-center">
              <icon name="badge-percent-light-teal" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_standard.discount_value && supplier.discounts.membership_standard.discount_type_id == 2"></icon>
              <icon name="badge-percent-light-red" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_enhanced.discount_value && supplier.discounts.membership_enhanced.discount_type_id == 2"></icon>
              <icon name="badge-percent-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 2"></icon>
              <icon name="sterling-sign-light-teal" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_standard.discount_value && supplier.discounts.membership_standard.discount_type_id == 1"></icon>
              <icon name="sterling-sign-light-red" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_enhanced.discount_value && supplier.discounts.membership_enhanced.discount_type_id == 1"></icon>
              <icon name="sterling-sign-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 1"></icon>
              Up to {{ formatDiscount(highestDiscount.value, highestDiscount.typeId) }} off
            </div>
          </div>
          <div v-if="supplier.phone" class="flex items-center align-middle mb-4">
            <icon name="phone-light" class="text-teal icon w-5 h-1 mr-2" :center="false"></icon>
            {{ supplier.phone }}
          </div>
          <div v-if="supplier.email" class="flex items-center align-middle mb-4">
            <icon name="envelope-light" class="text-teal icon w-5 h-1 mr-2" :center="false"></icon>
            <a :href="`mailto:${supplier.email}`" target="_blank" class="text-teal no-underline hover:underline">Send Email</a>
          </div>
          <div v-if="supplier.website" class="flex items-center align-middle mb-4">
            <icon name="arrow-up-right-from-square-light" class="text-teal icon w-5 h-1 mr-2" :center="false"></icon>
            <a :href="`${supplier.website}`" target="_blank" class="text-teal no-underline hover:underline">Visit Website</a>
          </div>
        </div>
        <div class="w-full md:w-1/2 px-6 border-r">
          <h1 class="text-2xl mb-6 font-semibold">{{ supplier.name }}</h1>
          <div id="supplier-content" class="mb-6 editor-content">
            <module-content :supplier="supplier" />
          </div>
        </div>
        <div class="w-full md:w-1/3 mb-6 pl-6">
          <div class="mb-8 rounded-lg border border-teal">
            <h3 class="px-4 py-2 rounded-t-lg bg-teal text-white" v-if="supplier.discount_section_type == 1">Guild Discount</h3>
            <h3 class="px-4 py-2 rounded-t-lg bg-teal text-white" v-else-if="supplier.discount_section_type == 2">Partnership Benefits</h3>
            <div class="p-4 leading-normal text-sm editor-content">
              <div v-if="supplier.discount_intro" v-html="supplier.discount_intro" class="mb-4"></div>
              <div v-if="supplier.discounts.membership_standard.discount_value" class="flex items-center align-middle mb-3 font-semibold">
                <icon name="badge-percent-light-teal" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_standard.discount_type_id == 2"></icon>
                <icon name="sterling-sign-light-teal" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_standard.discount_type_id == 1"></icon>
                Standard Membership = {{ formatDiscount(supplier.discounts.membership_standard.discount_value, supplier.discounts.membership_standard.discount_type_id) }} off
              </div>
              <div v-if="supplier.discounts.membership_enhanced.discount_value" class="flex items-center align-middle mb-3 font-semibold">
                <icon name="badge-percent-light-red" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_enhanced.discount_type_id == 2"></icon>
                <icon name="sterling-sign-light-red" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_enhanced.discount_type_id == 1"></icon>
                Enhanced Membership = {{ formatDiscount(supplier.discounts.membership_enhanced.discount_value, supplier.discounts.membership_enhanced.discount_type_id) }} off
              </div>
              <div v-if="supplier.discounts.membership_premium.discount_value" class="flex items-center align-middle mb-3 font-semibold">
                <icon name="badge-percent-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 2"></icon>
                <icon name="sterling-sign-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 1"></icon>
                Premium Membership = {{ formatDiscount(supplier.discounts.membership_premium.discount_value, supplier.discounts.membership_premium.discount_type_id) }} off
              </div>
              <div class="flex items-center" v-if="supplier.discount_section_type == 1 && supplier.discounts.general.code">
                <icon name="ticket-thin-teal" class="icon w-4 h-1 mr-2" :center="false"></icon>
                <span id="discount_code" class="font-bold">{{ supplier.discounts.general.code }}</span>
              </div>
              <div v-if="supplier.discount_outro" v-html="supplier.discount_outro" class="mt-1"></div>
            </div>
          </div>
          <!-- <div class="mb-8">
            <h4 class="mb-2">Company / Product Rated by Head Office</h4>
            <star-rating-component :active="true" :fixed="false" :value="supplier.user_star_rating" @save="saveStarRating" />
          </div> -->
          <div class="mb-8">
            <h4 class="mb-4">Tags</h4>
            <p class="mb-4">Categories</p>
            <div class="mb-6">
              <span v-for="category in supplier.categories" :key="category.id" class="inline-block text-white rounded-full text-sm mr-2 mb-2 px-3 py-2" style="background-color: #4ebdb4">
                <span class="inline-block whitespace-no-wrap">{{ category.name }}</span>
              </span>
            </div>
            <p class="mb-4">Services</p>
            <div class="mb-6">
              <span v-for="service in supplier.services" :key="service.id" class="inline-block text-white rounded-full text-sm mr-2 mb-2 px-3 py-2" style="background-color: #39d7e2">
                <span class="inline-block whitespace-no-wrap">{{ service.name }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { mapState } from 'vuex'
import StarRatingComponent from '../../components/forms/StarRatingComponent.vue'
import ModuleContent from '../../components/Suppliers/ModuleContent.vue'
export default {
  components: {
    StarRatingComponent,
    ModuleContent
  },
  data () {
    return {
      loaded: false,
      supplierId: null,
      supplier: null,
      ratings: null,
      userCanRate: null,
      savingStarRating: false
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    officeAccess: function () {
      // Access is currently restricted to Guild network only
      if (this.selectedOffice && this.selectedOffice.network_id === 2) {
        return true
      }

      return false
    },
    breadcrumbs: function () {
      if (!this.supplier) {
        return ''
      }
      let steps = [
        'A-Z of Suppliers'
      ]

      if (this.supplier.categories) {
        steps.push(this.supplier.categories[0].name)
      }

      if (this.supplier.services) {
        steps.push(this.supplier.services[0].name)
      }

      steps.push(this.supplier.name)

      return steps.join(' > ')
    },
    highestDiscount: function () {
      if (!this.supplier) {
        return 0
      }

      let highestDiscount = {
        value: 0,
        typeId: null
      }

      for (let key in this.supplier.discounts) {
        if ((this.supplier.discounts[key].discount_value || 0) > highestDiscount.value) {
          highestDiscount = {
            value: this.supplier.discounts[key].discount_value,
            typeId: this.supplier.discounts[key].discount_type_id
          }
        }
      }

      return highestDiscount
    }
  },
  watch: {
  },
  created () {
    this.supplierId = this.$route.params.supplierId ? this.$route.params.supplierId : null
    this.getSupplier()
    this.getUserRating()
  },
  methods: {
    getSupplier () {
      if (!this.officeAccess || !this.supplierId) {
        return
      }

      return v2Api.get(`suppliers/${this.supplierId}`).then((response) => {
        this.supplier = response.data
      }).finally(() => {
        this.loaded = true
      })
    },
    getUserRating () {
      let input = {
        'supplier_id': this.supplierId,
        'user_only': 1
      }
      return v2Api.get(`supplier-star-ratings`, { params: input }).then((response) => {
        this.ratings = response.data
        this.userCanRate = this.ratings.length === 0 && !this.supplier.admin_rating_override
      }).finally(() => {
        this.loaded = true
      })
    },
    saveStarRating (rating) {
      if (!this.officeAccess || !this.supplierId) {
        return
      }

      this.savingStarRating = true
      this.supplier.user_star_rating = rating

      let input = {
        'supplier_id': this.supplierId,
        'rating': rating,
        'admin_rating_override': false
      }

      return v2Api.post(`supplier-star-ratings`, input).then((response) => {
        this.supplier.star_rating = response.data
      }).finally(() => {
        this.savingStarRating = false
        this.userCanRate = false
      })
    },
    formatDiscount (value, typeId) {
      if (typeId === 2) {
        return value + '%'
      }

      return '£' + value
    }
  }
}
</script>

<style lang="sass">
#supplier-content
  p
    @apply text-sm leading-normal mb-6
  h3
    @apply mb-2
</style>
